import { useTranslations } from '@vocab/react';
import { Box, Button, ButtonLink, Stack, Text } from 'braid-design-system';
import type { FC } from 'react';

import type { TrackingData } from 'src/components/app/FilterSideBar/useFilters';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { Display } from 'src/libs/Display';
import {
  ApplicationCardsIllustration,
  CactusIllustration,
  JigsawPuzzleIllustration,
} from 'src/libs/Illustrations';
import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import { trackCandidateManagementTalentSearchViewRecommendedCandidatesBtnPressed } from 'src/utils/events';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';

import translations from './.vocab';
import { EmptyStateLayout } from './EmptyStateLayout';

interface ActiveJobEmptyStateProps {
  hasNoCandidates: boolean;
  jobId: number;
  bucket: BUCKETS;
}
const ActiveJobEmptyState: FC<ActiveJobEmptyStateProps> = ({
  hasNoCandidates,
  jobId,
  bucket,
}) => {
  const { t } = useTranslations(translations);
  const { router } = useAppConfig();
  const { getLabelFromBucket } = useBucketLabel();
  const [, setQueryQueryParams] = useQueryParamsStore();

  if (hasNoCandidates) {
    return (
      <EmptyStateLayout
        icon={<CactusIllustration width={128} height={128} />}
        messageHeading={t('No applications yet')}
      >
        <Display mobile tablet>
          <Text size="small" tone="secondary" align="center">
            {t(
              "While you're waiting for applications, check out candidates we've found who are a good match for your job. View their CVs and invite them to apply to your job ad.",
            )}
          </Text>
        </Display>
        <Display desktop>
          <Text size="standard" tone="secondary" align="center">
            {t(
              "While you're waiting for applications, check out candidates we've found who are a good match for your job. View their CVs and invite them to apply to your job ad.",
            )}
          </Text>
        </Display>
        <Box>
          <ButtonLink
            variant="solid"
            role="button"
            size="small"
            href={router.recommendedCandidates({ jobId })}
            onClick={() =>
              trackCandidateManagementTalentSearchViewRecommendedCandidatesBtnPressed()
            }
          >
            {t('View Candidate Matches')}
          </ButtonLink>
        </Box>
      </EmptyStateLayout>
    );
  }

  if (bucket === BUCKETS.INBOX) {
    const textDescription = (
      <>
        {t('Look in your other folders to see your applications.')}
        <br />
        {t(
          'Need more options? Check out candidates on SEEK who are a good match for your job.',
        )}
      </>
    );
    return (
      <EmptyStateLayout
        icon={<ApplicationCardsIllustration width={128} height={128} />}
        messageHeading={t("You've moved applications out of bucketLabel", {
          bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
        })}
      >
        <Display mobile tablet>
          <Text size="small" tone="secondary" align="center">
            {textDescription}
          </Text>
        </Display>
        <Display desktop>
          <Text size="standard" tone="secondary" align="center">
            {textDescription}
          </Text>
        </Display>
        <Box>
          <ButtonLink
            variant="solid"
            role="button"
            href={router.recommendedCandidates({ jobId })}
            onClick={() => {}}
            size="small"
          >
            {t('View Candidate Matches')}
          </ButtonLink>
        </Box>
      </EmptyStateLayout>
    );
  }

  return (
    <EmptyStateLayout
      icon={<ApplicationCardsIllustration width={128} height={128} />}
      messageHeading={t("You've moved applications out of bucketLabel", {
        bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
      })}
    >
      <Display mobile tablet>
        <Text size="small" tone="secondary" align="center">
          {t('Look in your other folders to see your applications.')}
        </Text>
      </Display>
      <Display desktop>
        <Text size="standard" tone="secondary" align="center">
          {t('Look in your other folders to see your applications.')}
        </Text>
      </Display>
      <Box>
        <Button
          variant="solid"
          onClick={() => setQueryQueryParams.navigateToBucket(BUCKETS.INBOX)}
          size="small"
        >
          {t('Go to inbox')}
        </Button>
      </Box>
    </EmptyStateLayout>
  );
};

interface EmptyStateProps {
  bucket: BUCKETS;
  preQuestionnaireFilterTotal: number;
  isMustHaveToggleSelected: boolean;
  searchText: string;
  resetFilters: (trackingData: TrackingData) => void;
  job: {
    jobId: number;
    isActive: boolean;
    hasNoCandidates: boolean;
  };
}
export const EmptyState: FC<EmptyStateProps> = ({
  bucket,
  preQuestionnaireFilterTotal,
  isMustHaveToggleSelected,
  searchText,
  resetFilters,
  job,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();

  if (searchText) {
    return (
      <EmptyStateLayout
        icon={<JigsawPuzzleIllustration width={128} height={128} />}
        messageHeading={t('No matching search results')}
      >
        <Text size="small" align="center">
          {t("Check that you've entered the right search term.")}
        </Text>
      </EmptyStateLayout>
    );
  }

  const filtersApplied = preQuestionnaireFilterTotal > 0;
  if (filtersApplied && isMustHaveToggleSelected) {
    return (
      <EmptyStateLayout
        icon={<JigsawPuzzleIllustration width={128} height={128} />}
        messageHeading={t(
          'There are no applications that match your filter selection',
        )}
      >
        <Display mobile tablet>
          <Stack space="small">
            <Text size="small" tone="secondary" align="center">
              {t(
                'applicationCount application/s in your bucketLabel do not meet your must-have requirements.',
                {
                  bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
                  applicationCount: preQuestionnaireFilterTotal,
                },
              )}
            </Text>
            <Text size="small" tone="secondary" align="center">
              {t('Turn off the must-have filter to view these application/s.', {
                applicationCount: preQuestionnaireFilterTotal,
              })}
            </Text>
          </Stack>
        </Display>
        <Display desktop>
          <Stack space="small">
            <Text tone="secondary" align="center">
              {t(
                'applicationCount application/s in your bucketLabel do not meet your must-have requirements.',
                {
                  bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
                  applicationCount: preQuestionnaireFilterTotal,
                },
              )}
            </Text>
            <Text tone="secondary" align="center">
              {t('Turn off the must-have filter to view these application/s.', {
                applicationCount: preQuestionnaireFilterTotal,
              })}
            </Text>
          </Stack>
        </Display>
        <Box>
          <Button
            variant="solid"
            onClick={() =>
              resetFilters({
                eventName: 'clear_filters_pressed',
                actionOrigin: 'candidate listing - centre cta with must have',
              })
            }
          >
            {t('Clear all filters')}
          </Button>
        </Box>
      </EmptyStateLayout>
    );
  }

  if (filtersApplied) {
    return (
      <EmptyStateLayout
        icon={<JigsawPuzzleIllustration width={128} height={128} />}
        messageHeading={t(
          'There are no applications that match your filter selection',
        )}
      >
        <Display mobile tablet>
          <Text size="small" tone="secondary" align="center">
            {t('Change or remove filters to broaden your results.')}
          </Text>
        </Display>
        <Display desktop>
          <Text tone="secondary" align="center">
            {t('Change or remove filters to broaden your results.')}
          </Text>
        </Display>
        <Box>
          <Button
            variant="solid"
            onClick={() =>
              resetFilters({
                eventName: 'clear_filters_pressed',
                actionOrigin:
                  'candidate listing - centre cta without must have',
              })
            }
          >
            {t('Clear all filters')}
          </Button>
        </Box>
      </EmptyStateLayout>
    );
  }

  if (job.isActive) {
    return (
      <ActiveJobEmptyState
        hasNoCandidates={job.hasNoCandidates}
        jobId={job.jobId}
        bucket={bucket}
      />
    );
  }

  return (
    <EmptyStateLayout
      icon={<CactusIllustration width={128} height={128} />}
      messageHeading={t('No applications in {status}', {
        status: getLabelFromBucket(bucket).toLocaleLowerCase(),
      })}
    />
  );
};
